import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServiceTemplateService } from '../../../core/services/service-template.service';
import { ServiceChangeRequestService } from '../../../core/services/service-change-request.service';
import { AuthService } from '../../../core/services/auth.service';
import {
  IServiceTemplateSummary,
  ServiceTemplateStatus,
} from '../../../core/models/service-template/service-template.model';
import { IServiceCategory } from '../../../core/models/service-category.model';
import {
  IIrembogovBasicLabelKeyPair,
  RolesEnum,
} from '@irembo-andela/irembogov3-common';
import { Router } from '@angular/router';

@Component({
  selector: 'irembogov-service-templates',
  templateUrl: './service-templates.component.html',
  styleUrls: ['./service-templates.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ServiceTemplatesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  userActiveRole?: string;
  RolesEnum = RolesEnum;
  ServiceTemplateStatus = ServiceTemplateStatus;

  templates: IServiceTemplateSummary[] = [];
  categories: IServiceCategory[] = [];
  categoryMap: { [key: string]: string } = {};
  isLoading = false;

  // Filter options
  templateStatuses: IIrembogovBasicLabelKeyPair<ServiceTemplateStatus | null>[] =
    [
      {
        label: 'All',
        key: null,
        icon: 'fas fa-list-alt',
      },
      {
        label: 'Draft',
        key: ServiceTemplateStatus.DRAFT,
        icon: 'fas fa-edit',
      },
      {
        label: 'Published',
        key: ServiceTemplateStatus.PUBLISHED,
        icon: 'fas fa-check-circle',
      },
    ];

  selectedStatus: ServiceTemplateStatus | null = null;
  selectedCategory: string | null = '';
  searchTerm = '';

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  // Delete confirmation modal
  showDeleteModal = false;
  templateToDelete: { id: string; name: string } | null = null;

  // Publish confirmation modal
  showPublishModal = false;
  templateToPublish: { id: string; name: string } | null = null;

  // Unpublish confirmation modal
  showUnpublishModal = false;
  templateToUnpublish: { id: string; name: string } | null = null;

  constructor(
    private serviceTemplateService: ServiceTemplateService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializeRole();
    this.loadCategories();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeRole() {
    this.authService.userActiveRole$
      .pipe(takeUntil(this.destroy$))
      .subscribe(newRole => {
        if (newRole === 'UNKNOWN_ROLE') {
          this.setFirstAllowedRole();
        } else {
          this.userActiveRole = newRole;

          if (this.userActiveRole !== RolesEnum.ROLE_IREMBOGOV_ADMIN) {
            this.router.navigate(['services']);
          }
        }
      });
  }

  private async setFirstAllowedRole() {
    const firstAllowedRole = await this.authService.firstAllowedRole();
    if (firstAllowedRole) {
      this.userActiveRole = firstAllowedRole;
    }

    if (this.userActiveRole !== RolesEnum.ROLE_IREMBOGOV_ADMIN) {
      this.router.navigate(['services']);
    }
  }

  private loadCategories() {
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.categories = response.data || [];
        this.categoryMap = this.categories.reduce((map, category) => {
          map[category.id] = category.name;
          return map;
        }, {} as { [key: string]: string });
      });
  }

  loadTemplates() {
    this.isLoading = true;

    const queryParams = this.serviceTemplateService.buildQueryParams({
      page: this.currentPage,
      size: this.pageSize,
      status: this.selectedStatus || undefined,
      categoryId: this.selectedCategory || undefined,
      search: this.searchTerm || undefined,
      sortBy: 'name',
      sortDirection: 'asc',
    });

    this.serviceTemplateService
      .getTemplates(queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.templates = response.data.content || [];
          this.totalElements = response.data.totalElements || 0;
          this.totalPages = response.data.totalPages || 0;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  onStatusChange(status: ServiceTemplateStatus | null) {
    this.selectedStatus = status;
    this.currentPage = 0;
    this.loadTemplates();
  }

  onCategoryChange(categoryId: string | null) {
    this.selectedCategory = categoryId;
    this.currentPage = 0;
    this.loadTemplates();
  }

  onSearchChange() {
    this.currentPage = 0;
    this.loadTemplates();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadTemplates();
  }

  canManageTemplates(): boolean {
    return this.userActiveRole === RolesEnum.ROLE_IREMBOGOV_ADMIN;
  }

  canUseTemplates(): boolean {
    return this.userActiveRole === RolesEnum.ROLE_SERVICE_MANAGER;
  }

  get filteredCategories() {
    return this.categories.filter(category =>
      this.templates.some(template => template.categoryId === category.id)
    );
  }

  showPublishConfirmation(templateId: string, templateName: string) {
    this.templateToPublish = { id: templateId, name: templateName };
    this.showPublishModal = true;
  }

  confirmPublish() {
    if (this.templateToPublish) {
      this.serviceTemplateService
        .publishTemplate(this.templateToPublish.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTemplates();
            this.cancelPublish();
          },
          error: () => {
            // Handle error
            this.cancelPublish();
          },
        });
    }
  }

  cancelPublish() {
    this.showPublishModal = false;
    this.templateToPublish = null;
  }

  showUnpublishConfirmation(templateId: string, templateName: string) {
    this.templateToUnpublish = { id: templateId, name: templateName };
    this.showUnpublishModal = true;
  }

  confirmUnpublish() {
    if (this.templateToUnpublish) {
      this.serviceTemplateService
        .unpublishTemplate(this.templateToUnpublish.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTemplates();
            this.cancelUnpublish();
          },
          error: () => {
            // Handle error
            this.cancelUnpublish();
          },
        });
    }
  }

  cancelUnpublish() {
    this.showUnpublishModal = false;
    this.templateToUnpublish = null;
  }

  showDeleteConfirmation(templateId: string, templateName: string) {
    this.templateToDelete = { id: templateId, name: templateName };
    this.showDeleteModal = true;
  }

  confirmDelete() {
    if (this.templateToDelete) {
      this.serviceTemplateService
        .deleteTemplate(this.templateToDelete.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTemplates();
            this.cancelDelete();
          },
          error: () => {
            // Handle error
            this.cancelDelete();
          },
        });
    }
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.templateToDelete = null;
  }
}
